import React, { useEffect, useState } from "react";
import {
  fetchReadingPassages, deleteReading,
  fetchCategories, fetchDifficulties,
  createCategory, updateCategory, deleteCategory,
  createDifficulty, updateDifficulty, deleteDifficulty,
} from "./adminReadingService";
import ReadingForm from "./ReadingForm";
import "../../blog/tongueTwister.css";

const MetaManager = ({ title, items, onCreate, onUpdate, onDelete }) => {
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [editingId, setEditingId] = useState(null);

  const startEdit = (item) => { setEditingId(item.id); setName(item.name); setSortOrder(item.sortOrder ?? ""); };
  const reset = () => { setEditingId(null); setName(""); setSortOrder(""); };

  const handleSave = async () => {
    if (!name.trim()) return;
    const payload = { name: name.trim(), sortOrder: sortOrder !== "" ? Number(sortOrder) : null };
    if (editingId) {
      await onUpdate(editingId, payload);
    } else {
      await onCreate(payload);
    }
    reset();
  };

  return (
    <div style={{ marginBottom: 24, background: "#f9f9f9", borderRadius: 8, padding: 16 }}>
      <h4 style={{ margin: "0 0 12px" }}>{title}</h4>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          placeholder="Name" value={name} onChange={(e) => setName(e.target.value)}
          style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" }}
        />
        <input
          type="number" placeholder="Sort" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}
          style={{ width: 70, padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" }}
        />
        <button onClick={handleSave}>{editingId ? "Update" : "Add"}</button>
        {editingId && <button onClick={reset}>Cancel</button>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {items.map((item) => (
          <span key={item.id} style={{ background: "#fff", border: "1px solid #ddd", borderRadius: 999, padding: "4px 12px", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            {item.name}
            <button onClick={() => startEdit(item)} style={{ background: "none", border: "none", cursor: "pointer", color: "#019095", fontSize: 12 }}>✎</button>
            <button onClick={() => onDelete(item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#e53935", fontSize: 12 }}>✕</button>
          </span>
        ))}
      </div>
    </div>
  );
};

const AdminReadingPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("passages"); // "passages" | "meta"

  const [search, setSearch] = useState("");
  const [difficultyId, setDifficultyId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [categories, setCategories] = useState([]);
  const [difficulties, setDifficulties] = useState([]);

  const loadMeta = () => {
    fetchCategories().then((r) => { if (r.status === 200) setCategories(r.data); });
    fetchDifficulties().then((r) => { if (r.status === 200) setDifficulties(r.data); });
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { if (tab === "passages") loadItems(); }, [page, search, difficultyId, categoryId, tab]);

  const loadItems = async () => {
    setLoading(true);
    const params = { page, size: 20 };
    if (search) params.search = search;
    if (difficultyId) params.difficultyId = difficultyId;
    if (categoryId) params.categoryId = categoryId;
    const res = await fetchReadingPassages(params);
    if (res.status === 200 && res.data) {
      setItems(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
    } else {
      setItems([]);
      alert(res.message || "Failed to load");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this reading passage?")) return;
    const res = await deleteReading(id);
    if (res.status === 200 || res.status === 204) loadItems();
    else alert(res.message);
  };

  const onSaved = () => { setShowForm(false); setEditing(null); loadItems(); };
  const handleFilterChange = (setter) => (e) => { setPage(0); setter(e.target.value); };

  // Meta CRUD handlers
  const handleCategoryCreate = async (data) => { await createCategory(data); loadMeta(); };
  const handleCategoryUpdate = async (id, data) => { await updateCategory(id, data); loadMeta(); };
  const handleCategoryDelete = async (id) => { if (!window.confirm("Delete category?")) return; await deleteCategory(id); loadMeta(); };

  const handleDifficultyCreate = async (data) => { await createDifficulty(data); loadMeta(); };
  const handleDifficultyUpdate = async (id, data) => { await updateDifficulty(id, data); loadMeta(); };
  const handleDifficultyDelete = async (id) => { if (!window.confirm("Delete difficulty?")) return; await deleteDifficulty(id); loadMeta(); };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Reading Passages</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setTab("passages")} style={{ opacity: tab === "passages" ? 1 : 0.5 }}>Passages</button>
          <button onClick={() => setTab("meta")} style={{ opacity: tab === "meta" ? 1 : 0.5 }}>Manage Categories & Difficulties</button>
          {tab === "passages" && <button onClick={() => { setEditing(null); setShowForm(true); }}>+ New Passage</button>}
        </div>
      </div>

      {showForm && (
        <ReadingForm
          passage={editing}
          categories={categories}
          difficulties={difficulties}
          onSaved={onSaved}
          onCancel={() => setShowForm(false)}
        />
      )}

      {tab === "meta" && (
        <>
          <MetaManager
            title="Categories"
            items={categories}
            onCreate={handleCategoryCreate}
            onUpdate={handleCategoryUpdate}
            onDelete={handleCategoryDelete}
          />
          <MetaManager
            title="Difficulties"
            items={difficulties}
            onCreate={handleDifficultyCreate}
            onUpdate={handleDifficultyUpdate}
            onDelete={handleDifficultyDelete}
          />
        </>
      )}

      {tab === "passages" && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
            <input
              type="text" placeholder="Search by title..."
              value={search} onChange={handleFilterChange(setSearch)}
              style={{ flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}
            />
            <select value={difficultyId} onChange={handleFilterChange(setDifficultyId)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
              <option value="">All Levels</option>
              {difficulties.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={categoryId} onChange={handleFilterChange(setCategoryId)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
              <option value="">All Categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : (
            <table className="announcement-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Difficulty</th>
                  <th>Category</th>
                  <th>Sort</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan="5">No passages found</td></tr>
                ) : (
                  items.map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.title}</td>
                      <td>{typeof p.difficulty === "object" ? p.difficulty?.name : p.difficulty || "—"}</td>
                      <td>{typeof p.category === "object" ? p.category?.name : p.category || "—"}</td>
                      <td style={{ textAlign: "center" }}>{p.sortOrder ?? "—"}</td>
                      <td>
                        <button onClick={() => { setEditing(p); setShowForm(true); }}>Edit</button>
                        <button className="delete-btn" onClick={() => handleDelete(p.id)}>Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {totalPages > 1 && (
            <div style={{ marginTop: 20 }}>
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
              <span style={{ margin: "0 10px" }}>Page {page + 1} / {totalPages}</span>
              <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminReadingPage;
